import useQueryWithTracing, { type ExtendedQueryResult } from '../../useQueryWithTracing';
import surveyQuery from './Survey.query.graphql';
import type {
  SurveyFragmentFragment,
  SurveyQuery,
  SurveyQueryVariables
} from '@aurora/shared-generated/types/graphql-types';
import type {
  AssociatedImage,
  SurveyProperties,
  SurveyQuestion
} from '@aurora/shared-generated/types/graphql-schema-types';

export type FilteredSurveyWithFinalQuestionCountAndProperties = SurveyFragmentFragment &
  Pick<SurveyProperties, 'surveyBrandName' | 'surveyCommunityName' | 'surveyHeaderBgColor'> & {
    finalQuestionCount: number;
    surveyBrandingLogo?: AssociatedImage;
  };

/**
 * Hook to fetch survey data
 *
 * @author Adam Ayres
 */
export default function useSurvey(skip: boolean): ExtendedQueryResult<
  SurveyQuery,
  SurveyQueryVariables
> & {
  computedSurvey: FilteredSurveyWithFinalQuestionCountAndProperties;
} {
  const queryResult = useQueryWithTracing<SurveyQuery, SurveyQueryVariables>(module, surveyQuery, {
    skip
  });

  const { data } = queryResult;
  let filteredSurveyWithFinalQuestionCountAndProperties: FilteredSurveyWithFinalQuestionCountAndProperties =
    null;

  if (data) {
    function traverseSteps(
      survey: SurveyFragmentFragment,
      question: SurveyQuestion,
      currentCount: number
    ): number {
      const nextBranch =
        question.branches.find(branch => branch.trigger === 'default_flow') ?? question.branches[0];

      if (nextBranch) {
        const nextQuestion = survey.questions.find(q => q.id === nextBranch.target);
        if (nextQuestion) {
          return traverseSteps(survey, nextQuestion, currentCount + 1);
        }
        // assume this is the last question
        return currentCount + 1;
      }

      // this should never be hit, but just in case...
      return currentCount;
    }

    const surveyDisabledInputs = data.community.surveyProperties.surveyDisabledInputs ?? [];
    const { surveyCommunityName, surveyBrandName, surveyHeaderBgColor } =
      data.community.surveyProperties;

    function getTruncatedQuestionId(question: SurveyQuestion): string {
      return question.id.split('.').at(-1);
    }

    const filteredSurvey: SurveyFragmentFragment = {
      ...data?.valueSurvey,
      questions: data?.valueSurvey?.questions
        .filter(question => !surveyDisabledInputs.includes(getTruncatedQuestionId(question)))
        .map(question => ({
          ...question,
          id: getTruncatedQuestionId(question),
          answers: question.answers.filter(
            answer =>
              !surveyDisabledInputs.includes(`${getTruncatedQuestionId(question)}.${answer.id}`)
          )
        }))
    };

    filteredSurveyWithFinalQuestionCountAndProperties = {
      ...filteredSurvey,
      surveyCommunityName,
      surveyBrandName,
      surveyHeaderBgColor,
      surveyBrandingLogo: data.surveyBrandingLogo ?? null,
      finalQuestionCount: traverseSteps(filteredSurvey, filteredSurvey.questions[0], 1)
    };
  }

  return { ...queryResult, computedSurvey: filteredSurveyWithFinalQuestionCountAndProperties };
}
